import styled, { css } from "styled-components";
import { baseCaption, defaultFont } from "../../theme/typography";
import {
  blue300,
  curiousBlue300,
  cinnabar300,
  black600,
  selago,
  white,
} from "../../theme/colors";
import { bold } from "../../theme/typeface";
import { Link } from "react-router-dom";
import themeSizes from "../../theme/themeSizes";

export const DrawerBackdrop = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(15, 23, 42, 0.3);
    backdrop-filter: blur(4px);
    z-index: 1250;
    opacity: ${({ show }) => (show ? "1" : "0")};
    pointer-events: ${({ show }) => (show ? "auto" : "none")};
    transition: opacity 0.25s ease-in-out;
  }
`;

export const DashboardWrapper = styled.div`
  position: relative;
  display: flex;
  min-height: 100vh;
  background-color: ${selago};

  /* Ensure sidebar tooltips display on top of the sidebar and content */
  .__react_component_tooltip {
    z-index: 9999 !important;
  }
`;

export const SideNavStyle = styled.aside`
  width: ${({ expand }) => (expand ? "260px" : "72px")};
  min-width: ${({ expand }) => (expand ? "260px" : "72px")};
  background-color: #ffffff;
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 1260;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    width: 270px;
    min-width: 270px;
    transform: ${({ expand }) => (expand ? "translateX(0)" : "translateX(-100%)")};
    box-shadow: ${({ expand }) => (expand ? "10px 0 30px rgba(15, 23, 42, 0.15)" : "none")};
  }

  .logo-container {
    height: 64px;
    display: flex;
    align-items: center;
    padding: 0 20px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  }

  img {
    max-height: 40px;
    object-fit: contain;
    transition: all 0.2s ease;
  }

  ul {
    flex: 1;
    margin: 0;
    padding: 16px 8px;
    list-style: none;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(203, 213, 225, 0.6);
      border-radius: 10px;
    }

    a {
      display: block;
      margin-bottom: 4px;
    }

    li {
      height: 44px;
      padding: 0 12px;
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      color: #64748b;
      transition: all 0.2s ease;
      font-weight: 500;
      ${baseCaption}

      svg {
        font-size: 20px;
        min-width: 20px;
        color: #94a3b8;
        transition: color 0.2s ease;
      }

      span {
        opacity: ${({ expand }) => (expand ? "1" : "0")};
        width: ${({ expand }) => (expand ? "auto" : "0")};
        overflow: hidden;
        text-overflow: ellipsis;
        padding-left: 10px;
        white-space: nowrap;
        transition: opacity 0.2s ease, width 0.2s ease;

        @media (max-width: 768px) {
          opacity: 1;
          width: auto;
        }
      }

      &:hover {
        background-color: rgba(71, 112, 193, 0.05);
        color: ${blue300};
        svg {
          color: ${blue300};
        }
      }

      &.active {
        background-color: rgba(71, 112, 193, 0.08);
        color: ${blue300};
        font-weight: 600;
        svg {
          color: ${blue300};
        }
      }
    }
  }
`;

export const ContentWrapper = styled.section`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background-color: ${selago};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    background-color: ${white};
  }
`;

export const HorizontalBar = styled.div`
  background: #ffffff;
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  height: 64px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 1200;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.01), 0 2px 4px -1px rgba(0, 0, 0, 0.01);

  span {
    display: inline-flex;
    align-items: center;
    color: #64748b;
    transition: color 0.2s ease;
    &:hover {
      color: ${blue300};
    }
    svg {
      font-size: 24px;
    }
  }
  
  & > div {
    display: flex;
    align-items: center;
    gap: 16px;

    svg {
      color: #64748b;
      cursor: pointer;
      font-size: 22px;
      transition: color 0.2s ease;
      &:hover {
        color: ${blue300};
      }
    }
  }
`;

export const ContentStyle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  & > div {
    flex: 1;
  }
`;

export const TitleStyle = styled.div`
  ${defaultFont}
  font-weight: ${bold};
  margin: ${({ margin }) => margin || 0};
`;

export const Button = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: ${(props) => props.background || "#212323"};
  color: ${(props) => props.color || "#FFFFFF"};
  font-size: ${(props) => props.fontSize || "15px"};
  font-weight: 500;
  padding: ${(props) => props.padding || "10px 18px"};
  margin-left: ${({ align }) => align === "right" && `12px`};
  margin-right: ${({ align }) => align === "left" && `12px`};
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

  svg {
    font-size: 18px !important;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(91, 127, 220, 0.15);
    filter: brightness(1.05);
    color: ${(props) => props.color || "#FFFFFF"};
    text-decoration: none;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
`;
export const UploadButtonBlock = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: ${(props) => props.position || "static"};
  right: ${(props) => props.right || "0px"};
  border: 1px solid ${(props) => props.borderColor || "transparent"};
  border-radius: 8px;
  background: ${(props) => props.background || curiousBlue300};
  color: ${(props) => props.color || "#FFFFFF"};
  font-size: ${(props) => props.fontSize || "14px"};
  font-weight: 500;
  padding: ${(props) => props.padding || "10px 18px"};
  margin-left: ${({ align }) =>
    (align === "right" && `12px`) ||
    (align === "to right" && `150px`) ||
    (align === "right end" && `650px`)};
  margin-right: ${({ align }) => align === "left" && `12px`};
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

  svg {
    font-size: 18px !important;
    vertical-align: middle;
    margin-right: 0;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(91, 127, 220, 0.15);
    filter: brightness(1.08);
    color: ${(props) => props.color || "#FFFFFF"};
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  ${(props) =>
    props.isDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.8;
      pointer-events: none;
      transform: none !important;
      box-shadow: none !important;
    `}

  @media (max-width: 995px) {
    margin-left: 0 !important;
    margin-right: 0 !important;
    white-space: nowrap !important;
    min-height: 42px !important;
    padding: 8px 16px !important;
    font-size: 13.5px !important;
    font-weight: 600 !important;
    box-sizing: border-box !important;
  }
`;

export const InvertButton = styled(Button)`
  color: #212323;
  background: #ffffff;
  border: 1px solid #212323;
  margin-left: ${({ align }) => align === "right" && `12px`};
  margin-right: ${({ align }) => align === "left" && `12px`};

  &:hover {
    background: #f8fafc;
    border-color: #0f172a;
    color: #212323;
  }
`;
export const TitleContainer = styled(TitleStyle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
`;

export const IftuButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;

    & > * {
      margin: 0 !important;
    }
  }
`;

export const ButtonContainer = styled.div`
  display: ${(props) => props.display || "flex"};
  align-items: ${(props) => props.alignItems || "center"};
  gap: ${(props) => props.gap || "12px"};
  margin-top: ${(props) => props.marginTop || "0px"};
  float: ${(props) => props.float || "auto"};

  @media ${themeSizes.mobile} {
    flex-direction: ${(props) => (props.rowOnMobile ? "row" : "column")};
    flex-wrap: ${(props) => (props.rowOnMobile ? "wrap" : "nowrap")};
    align-items: ${(props) => (props.rowOnMobile ? "center" : "stretch")};
    width: 100%;
    gap: 8px;

    & > a, & > button {
      margin: 0 !important;
      text-align: center;
      width: ${(props) => (props.rowOnMobile ? "auto" : "100%")};
      flex: ${(props) => (props.rowOnMobile ? "1 1 0" : "none")};
      box-sizing: border-box;
    }
  }
`;
export const Top = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  background-color: #529165;
  border-radius: 5px;
  padding: 5px;
  justify-content: space-between;
  width: 100%;
  // height: 10%;
  margin-bottom: 5%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 5%;
  height: 40px;
  align-items: center;
  align-content: center;
`;
export const ShareTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  padding: 5px;
`;
export const Logo = styled.img`
  width: 25px;
  height: 25px;
  border-radius: 25px;
`;
export const Middle = styled.div`
  width: 100%;
  background-color: transparent;
  // margin-bottom: 5%;
  justify-content: flex-end;
  float: right;
`;
export const Search = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  height: 35px;
  width: 100%;
  border-radius: 5px;
  background-color: white;
  height: 40px;
  border: 1px solid #d9d9d9;
  margin-bottom: 5%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;
export const Bottom = styled.div`
  width: 60px;
  height: 60px;
  background-color: transparent;
  background-color: #2273cd;
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  color: #fff;
  cursor: pointer;
  font-size: 24px;
  z-index: 1000;
`;

export const CardMain = styled.div`
  width: 100%;
  margin-bottom: 2%;
  margin-top: 1%;
  background-color: transparent;
`;

export const Main = styled.div`
  height: 100%;
  align-items: center;
  align-content: center;
`;

export const Cards = styled.div`
  height: 15%;
  width: 100%;
  border-radius: 5px;
  background-color: #ffffff;
  border: 1px solid #d1d1d1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 10px;
  margin-bottom: 20px;
`;

export const CardTitle = styled.p`
  font-weight: bold;
  font-size: 14px;
  color: black;
`;

export const RSID = styled.p`
  font-size: 12px;
  color: black;
`;

export const OUTLET = styled.p`
  text-align: right;
  font-size: 10px;
  font-style: italic;
  color: black;
`;

export const LOCATION = styled.p`
  font-size: 12px;
  color: black;
  font-style: italic;
`;

export const First = styled.div`
  align-items: flex-end;
`;
export const Second = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
`;
export const Sub = styled.div``;

export const SubShare = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const Third = styled.div`
  flex-direction: row;
  justify-content: space-between;
  margin-left: 2%;
  margin-bottom: 2%;
`;

export const CardButton = styled.div`
  height: 10%;
  display: flex;
  border: 1px solid #d1d1d1;
  border-radius: 5px;
  align-content: center;
  align-items: center;
  margin-bottom: 5%;
  box-shadow: 2px 2px 5px 0px #d1d1d1;
`;
export const CardEKYCButton = styled.div`
  height: 10%;
  display: flex;
  border: 1px solid #d1d1d1;
  border-radius: 5px;
  align-content: center;
  align-items: center;
  margin-bottom: 5%;
  background-color: #fff;
  box-shadow: 2px 2px 5px 0px #d1d1d1;
`;
export const CardButtonOpen = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
`;

export const CalenderDiv = styled.div`
  width: 30%;
  border-radius: 5px;
  border: 1px solid #d1d1d1;
  margin-right: 5%;
  padding: 5px;
  display: flex;
  align-items: center;
  margin-right: 10%;
  height: 40px;
  justify-content: space-between;
`;

export const Line = styled.div`
  width: 70%;
  display: flex;
  flex-direction: row;
  //   justify-content: center;
  align-items: center;
  margin-left: 5%;
  height: 40px;
`;

export const Location = styled.div`
  width: 90%;
  display: flex;
  flex: 0.5;
  flex-direction: row;
  //   justify-content: center;
  //   align-items: center;
  margin-left: 5%;
  //   height: 60px;
`;

export const Label = styled.p`
  marginright: 10px;
  margin: 0px;
  color: #9b9b9b;
`;

export const KYCstatusDone = styled.div`
  width: 50%;
  background-color: #33a853;
  display: flex;
  flex-direction: row;
  align-content: center;
  justify-content: "space-around";
  border-radius: 25px;
  align-items: center;
  margin-left: ${(props) => (props.marginRight ? "-1%" : "5%")};

`;
export const KYCstatusPending = styled.div`
  width: 50%;
  background-color: #f3b15f;
  display: flex;
  flex-direction: row;
  align-content: center;
  justify-content: "space-around";
  border-radius: 25px;
  align-items: center;
  margin-left: ${(props) => (props.marginRight ? "-1%" : "5%")};

`;

